/* ═══════════════════════════════════════════════════════════════
   body-layout — the ONE layout brain for SPEC BODY paragraphs.

   Sibling of `zone-layout.ts` (which owns L/C/R header/footer zones).
   Before this module, body layout was derived independently three
   times — word-engine (the .docx truth), the formatted preview, and
   the editor canvas — so "preview doesn't match export" was a
   structural guarantee, not a bug. This module makes every body
   decision ONCE, from the forma:

     · the CSI numbering scheme (which %N pattern each level prints)
     · the computed paragraph number for every block
     · indents in Word's model (wrap edge + hanging/first-line)
     · space before/after, keep rules, contextual spacing, alignment
     · the run properties a level imposes (bold/caps/italic/…)

   Consumers turn those decisions into their own output:
     · word-engine.ts     → OOXML (styles.xml + numbering.xml)
     · the Designer canvas → CSS
     · export preview / diff rendering → CSS

   Pure TypeScript: no Node, no DOM, no measurement. Anything that
   needs to MEASURE (pagination) injects its own measurer, exactly
   like zone-layout does.
   ═══════════════════════════════════════════════════════════════ */

import type { ContentBlock, LevelStyle, StandardConfig } from './types';
import { toTitleCase } from './zone-layout';

// ─── Units ───

/** 1 inch = 1440 twips. */
export const inToTwip = (inches: number): number => Math.round(inches * 1440);
/** 1 pt = 20 twips. */
export const ptToTwip = (pt: number): number => Math.round(pt * 20);
/** CSS px at the canvas' 96-DPI paper scale. */
export const inToPx = (inches: number): number => inches * 96;
export const ptToPx = (pt: number): number => (pt * 96) / 72;

// ─── The CSI numbering scheme ───

/** One level of the Word numbering definition (abstractNum ▸ lvl). */
export interface CsiLevelFormat {
  /** Word numFmt: decimal | decimalZero | upperLetter | lowerLetter | upperRoman | lowerRoman */
  numFmt: string;
  /** Word lvlText, e.g. "PART %1 - ", "%1.%2", "(%8)". */
  lvlText: string;
  /** Word suff: what separates the number from the text. null = Word's default (tab). */
  suff: string | null;
  /** Level 2 only: pad the counter to two digits ("1.01" vs "1.1"). */
  zeroPad?: boolean;
}

/** The out-of-the-box CSI MasterFormat scheme (PART 1 - / 1.01 / A. / 1. / a. / …). */
export const CSI_LEVELS_DEFAULT: CsiLevelFormat[] = [
  { numFmt: 'decimal', lvlText: 'PART %1 - ', suff: 'space' },
  { numFmt: 'decimal', lvlText: '%1.%2', suff: null, zeroPad: true },
  { numFmt: 'upperLetter', lvlText: '%3.', suff: null },
  { numFmt: 'decimal', lvlText: '%4.', suff: null },
  { numFmt: 'lowerLetter', lvlText: '%5.', suff: null },
  { numFmt: 'decimal', lvlText: '%6)', suff: null },
  { numFmt: 'lowerLetter', lvlText: '%7)', suff: null },
  { numFmt: 'decimal', lvlText: '(%8)', suff: null },
  { numFmt: 'lowerLetter', lvlText: '(%9)', suff: null },
];

/** `special.numberFormats[i]` id → the level format it selects. */
const FORMAT_OVERRIDES: Record<string, Partial<CsiLevelFormat>> = {
  part_dash: { numFmt: 'decimal', lvlText: 'PART %1 - ', suff: 'space' },
  part_space: { numFmt: 'decimal', lvlText: 'PART %1 ', suff: 'space' },
  '1.01': { numFmt: 'decimal', lvlText: '%1.%2', suff: null, zeroPad: true },
  '1.1': { numFmt: 'decimal', lvlText: '%1.%2', suff: null, zeroPad: false },
};

/** Swappable levels 6-9: format id → lvlText for that level index. */
const LVL_TEXT_MAP: Record<number, Record<string, string>> = {
  5: { '1)': '%6)', '(1)': '(%6)' },
  6: { 'a)': '%7)', '(a)': '(%7)' },
  7: { '(1)_8': '(%8)', '1)_8': '%8)', '(1)': '(%8)', '1)': '%8)' },
  8: { '(a)_9': '(%9)', 'a)_9': '%9)', '(a)': '(%9)', 'a)': '%9)' },
};

/**
 * Resolve the forma's chosen numbering scheme. Identical logic to the
 * engine's former private `getCSILevels()` — this is now its only home.
 */
export function getCsiLevelFormats(config: StandardConfig | null | undefined): CsiLevelFormat[] {
  const nf = config?.special?.numberFormats;
  if (!nf) return CSI_LEVELS_DEFAULT.map((l) => ({ ...l }));

  const levels: CsiLevelFormat[] = CSI_LEVELS_DEFAULT.map((l) => ({ ...l }));
  for (let i = 0; i < nf.length && i < 9; i++) {
    const fid = nf[i];
    if (!fid) continue;
    if (i <= 1) {
      const ov = FORMAT_OVERRIDES[fid];
      if (ov) levels[i] = { ...levels[i], ...ov };
    } else if (i >= 5 && LVL_TEXT_MAP[i]) {
      const lt = LVL_TEXT_MAP[i][fid];
      if (lt) {
        levels[i] = {
          numFmt: fid.includes('a') ? 'lowerLetter' : 'decimal',
          lvlText: lt,
          suff: null,
        };
      }
    }
  }
  return levels;
}

// ─── Counter rendering (Word's numFmt) ───

const ROMAN: Array<[number, string]> = [
  [1000, 'm'],
  [900, 'cm'],
  [500, 'd'],
  [400, 'cd'],
  [100, 'c'],
  [90, 'xc'],
  [50, 'l'],
  [40, 'xl'],
  [10, 'x'],
  [9, 'ix'],
  [5, 'v'],
  [4, 'iv'],
  [1, 'i'],
];

function toRoman(n: number): string {
  if (n <= 0) return '';
  let out = '';
  let rest = n;
  for (const [v, s] of ROMAN) {
    while (rest >= v) {
      out += s;
      rest -= v;
    }
  }
  return out;
}

/** Word's a…z, aa…az, ba… sequence (never runs out, unlike a 26-item clamp). */
function toAlpha(n: number): string {
  if (n <= 0) return '';
  const letter = String.fromCharCode(97 + ((n - 1) % 26));
  return letter.repeat(Math.floor((n - 1) / 26) + 1);
}

/** Render one counter with a Word numFmt. */
export function formatCounter(n: number, numFmt: string): string {
  switch (numFmt) {
    case 'decimalZero':
      return String(n).padStart(2, '0');
    case 'upperLetter':
      return toAlpha(n).toUpperCase();
    case 'lowerLetter':
      return toAlpha(n);
    case 'upperRoman':
      return toRoman(n).toUpperCase();
    case 'lowerRoman':
      return toRoman(n);
    case 'none':
      return '';
    default:
      return String(n);
  }
}

/** The effective numFmt for a level, folding in level 2's zeroPad switch. */
export function effectiveNumFmt(fmt: CsiLevelFormat): string {
  if (fmt.zeroPad === true && fmt.numFmt === 'decimal') return 'decimalZero';
  return fmt.numFmt;
}

/**
 * The `w:numFmt` to WRITE into numbering.xml for a level — and the ONE place
 * the bullet invariant is enforced:
 *
 *   numFmt="bullet"  ⟺  the level's lvlText carries NO %n placeholder.
 *
 * Word renders every `%n` in a level whose numFmt is `bullet` (or `none`) as
 * EMPTY, so a level defined as `lvlText="%5." numFmt="bullet"` prints a bare
 * "." — Josh's real 09 91 00 output, where H5 items printed "." and H6 ")".
 * A source document's heading list bullets levels 5-9 out of the box, so the
 * shape (lvlText) and the format (numFmt) must come from THIS one brain or
 * they drift apart exactly like that.
 *
 * Callers pass a level whose `lvlText` is set; a counted level ("%5.") can
 * never be a bullet, and an uncounted level ("•") is always one.
 */
export function numberingNumFmt(fmt: CsiLevelFormat): string {
  const eff = effectiveNumFmt(fmt);
  const counted = /%[1-9]/.test(fmt.lvlText || '');
  if (!counted) return 'bullet';
  return !eff || eff === 'bullet' || eff === 'none' ? 'decimal' : eff;
}

/**
 * The `w:suff` to WRITE into numbering.xml for a level — what separates the
 * number from the text. `suff: null` is not "no opinion": every consumer of
 * this scheme (the preview's renderLevelPrefix, the .dotx export, the
 * formatter) means Word's default TAB by it. Word only defaults to tab when
 * the element is ABSENT, so a source level that carries its own
 * `<w:suff w:val="nothing"/>` silently kept it while the forma said tab.
 * Writing this value on every level is what makes the printed document and
 * the preview agree.
 */
export function numberingSuff(fmt: CsiLevelFormat): 'tab' | 'space' | 'nothing' {
  return fmt.suff === 'space' ? 'space' : fmt.suff === 'nothing' ? 'nothing' : 'tab';
}

// ─── Numbering a document ───

export interface NumberedRef {
  /** The block this number belongs to. */
  id: string;
  /** 1-9. */
  level: number;
  /**
   * The printed paragraph number, trailing whitespace trimmed
   * ("PART 1 -", "1.01", "A.", "(a)"). Empty for continuation blocks
   * and for levels whose scheme prints nothing.
   */
  prefix: string;
  /** What separates the number from the text: Word's `suff`. */
  suffix: 'tab' | 'space' | 'nothing';
  /** Counter values 1-9 at this paragraph (1-based; 0 = not yet reached). */
  counters: number[];
  /**
   * MODULE MODE only. A level-2 article that declares no part: it belongs to
   * no part, so it prints NO number and the renderer shows a muted
   * placeholder instead (Josh's ruling, 2026-08-31 — an unassigned article
   * must never masquerade as an assigned one). Never set by
   * `computeNumbering`, so a spec is untouched.
   */
  unassigned?: boolean;
}

/**
 * Is this block part of the printed document? Excluded and un-included
 * blocks never reach the .docx, so they must never consume a number —
 * that mismatch is why the old preview drifted from the export.
 */
export function isRenderable(b: Pick<ContentBlock, 'included' | 'excluded'>): boolean {
  return b.included !== false && b.excluded !== true;
}

/**
 * Compute the CSI number for every block, exactly as Word will when the
 * forma's numbering definition is applied to the exported paragraphs.
 *
 * Rules (all mirror Word):
 *  - a paragraph at level L increments counter L and resets L+1…9
 *  - `continuation` blocks share the previous paragraph's number and are
 *    not counted (they carry no numPr in the export)
 *  - non-renderable blocks (excluded / not included) are skipped entirely
 *  - each `%N` in the level's lvlText renders counter N with LEVEL N's numFmt
 */
/**
 * Render ONE level's printed number from a counter state — the single place
 * "%N in this level's lvlText prints counter N with LEVEL N's numFmt" is
 * expressed. Shared by computeNumbering (a real document) and
 * sampleLevelNumbers (the review modal's per-level preview) so a preview can
 * never drift from what the export actually prints.
 */
function renderLevelPrefix(
  formats: CsiLevelFormat[],
  level: number,
  counters: number[]
): { prefix: string; suffix: 'tab' | 'space' | 'nothing' } {
  const fmt = formats[level - 1] || CSI_LEVELS_DEFAULT[level - 1];
  const prefix = fmt.lvlText
    .replace(/%([1-9])/g, (_m, d: string) => {
      const idx = Number(d) - 1;
      const src = formats[idx] || CSI_LEVELS_DEFAULT[idx];
      return formatCounter(counters[idx], effectiveNumFmt(src));
    })
    .replace(/\s+$/, '');
  return { prefix, suffix: numberingSuff(fmt) };
}

/**
 * The number each level 1-9 prints at counter 1 ("PART 1 -", "1.01", "A.",
 * "1.", "a.", …) under THIS forma's numbering scheme. Derived from the same
 * getCsiLevelFormats/renderLevelPrefix brain the export uses — never a second
 * hardcoded table. Index 0 = level 1.
 */
export function sampleLevelNumbers(config: StandardConfig | null | undefined): string[] {
  const formats = getCsiLevelFormats(config);
  return Array.from({ length: 9 }, (_, i) => {
    // Level i+1 reached for the first time: every ancestor counter is at 1,
    // this level's counter has just become 1, deeper levels are unreached.
    const counters = Array.from({ length: 9 }, (_, j) => (j <= i ? 1 : 0));
    return renderLevelPrefix(formats, i + 1, counters).prefix;
  });
}

export function computeNumbering(
  blocks: ContentBlock[],
  config: StandardConfig | null | undefined
): NumberedRef[] {
  const formats = getCsiLevelFormats(config);
  const counters = [0, 0, 0, 0, 0, 0, 0, 0, 0];
  const out: NumberedRef[] = [];

  for (const b of blocks) {
    const level = Math.max(1, Math.min(9, b.level || 1));
    if (!isRenderable(b)) {
      out.push({ id: b.id, level, prefix: '', suffix: 'nothing', counters: counters.slice() });
      continue;
    }
    if (b.continuation) {
      out.push({ id: b.id, level, prefix: '', suffix: 'nothing', counters: counters.slice() });
      continue;
    }
    counters[level - 1]++;
    for (let i = level; i < 9; i++) counters[i] = 0;

    const { prefix, suffix } = renderLevelPrefix(formats, level, counters);

    out.push({
      id: b.id,
      level,
      prefix,
      suffix,
      counters: counters.slice(),
    });
  }
  return out;
}

/** Convenience: id → NumberedRef. */
export function numberingById(refs: NumberedRef[]): Map<string, NumberedRef> {
  return new Map(refs.map((r) => [r.id, r]));
}

/** The part (1/2/3) a level-2 module article declares, read straight off the
 *  block — duplicated here (not imported from spec-modules.ts) so this file
 *  stays the shared/no-dependents brain spec-modules.ts already leans on. */
function moduleArticlePart(b: ContentBlock): number | undefined {
  const raw = (b as unknown as { modulePart?: unknown }).modulePart;
  return raw === 1 || raw === 2 || raw === 3 ? raw : undefined;
}

/**
 * MODULE MODE (Cohesion R1 Phase 1). A module document has no level-1 Part
 * block — the part a level-2 article belongs to is metadata (`modulePart`),
 * never a rendered block. This is `computeNumbering`'s twin for that shape:
 * counters[0] is SEEDED to the article's declared part (never incremented
 * by a level-1 block, because a module never has one), so the first article
 * of Part 2 prints "2.01" exactly the way a spec's own Part 2 would.
 * Level 2+ numbering inside a part is byte-for-byte the same loop
 * `computeNumbering` runs — this never touches that function or its
 * behavior for a spec.
 */
export function computeModuleNumbering(
  blocks: ContentBlock[],
  config: StandardConfig | null | undefined
): NumberedRef[] {
  const formats = getCsiLevelFormats(config);
  const counters = [0, 0, 0, 0, 0, 0, 0, 0, 0];
  const out: NumberedRef[] = [];
  // A paragraph PINNED under a part with no article yet (TWEAKS item 10)
  // has no article to number under: it, and every paragraph after it up to
  // the next article, prints the same muted placeholder an unassigned
  // article does. Builder-only — a saved module never carries a pin.
  let pinnedRun = false;

  for (const b of blocks) {
    const level = Math.max(1, Math.min(9, b.level || 1));
    if (level === 2) pinnedRun = false;
    else if (level > 2) {
      const pin = (b as unknown as { modulePin?: unknown }).modulePin;
      if (pin === 1 || pin === 2 || pin === 3) pinnedRun = true;
    }
    if (pinnedRun && level > 2) {
      out.push({
        id: b.id,
        level,
        prefix: '',
        suffix: 'nothing',
        counters: counters.slice(),
        unassigned: true,
      });
      continue;
    }
    if (!isRenderable(b) || b.continuation) {
      out.push({ id: b.id, level, prefix: '', suffix: 'nothing', counters: counters.slice() });
      continue;
    }
    if (level === 2) {
      // Seed the Part counter off the article's own declaration — only for
      // articles that actually render (Job A2: a non-rendering level-2
      // block, e.g. a continuation, must never reset counters[0]).
      const part = moduleArticlePart(b);
      if (part === undefined) {
        // UNASSIGNED (Josh's ruling, 2026-08-31). An article that declares
        // no part inherits NOTHING: it does not adopt the part that happens
        // to be running, it does not default to Part 1, and it consumes no
        // article number — so the first real Part 1 article is still 1.01
        // whatever sits above it. It prints no number at all; the editor
        // draws a muted placeholder off the `unassigned` flag. Its own
        // children still number from scratch (deeper counters reset), so a
        // list under an unassigned article reads A., B., C. as usual.
        for (let i = 2; i < 9; i++) counters[i] = 0;
        out.push({
          id: b.id,
          level,
          prefix: '',
          suffix: 'nothing',
          counters: counters.slice(),
          unassigned: true,
        });
        continue;
      }
      if (part !== counters[0]) {
        counters[0] = part;
        for (let i = 1; i < 9; i++) counters[i] = 0;
      }
    }
    counters[level - 1]++;
    for (let i = level; i < 9; i++) counters[i] = 0;

    const { prefix, suffix } = renderLevelPrefix(formats, level, counters);

    out.push({
      id: b.id,
      level,
      prefix,
      suffix,
      counters: counters.slice(),
    });
  }
  return out;
}

// ─── Paragraph layout per level ───

/** Every layout decision one spec level imposes on its paragraphs. */
export interface BodyLevelLayout {
  /** 1-9. */
  level: number;

  // ── Indentation, in Word's model ──
  /**
   * The WRAP EDGE (Word's `w:ind w:left`) in twips: where wrapped lines and
   * the paragraph text begin. = indent + hangingIndent, floored at 0.
   */
  leftTwips: number;
  /** Word's `w:hanging` in twips (≥ 0). Mutually exclusive with firstLineTwips. */
  hangingTwips: number;
  /** Word's `w:firstLine` in twips (≥ 0) — set when the forma dragged the
   *  first line PAST the numbering (a negative hanging indent). */
  firstLineTwips: number;

  // ── Vertical rhythm ──
  spaceBeforeTwips: number;
  spaceAfterTwips: number;
  /** `w:contextualSpacing` — drop space-before between same-style paragraphs. */
  contextualSpacing: boolean;
  keepWithNext: boolean;
  keepLinesTogether: boolean;

  // ── Alignment ──
  /** 'left' is Word's default and emits no <w:jc>. */
  justify: 'left' | 'center' | 'right' | 'justify';

  // ── Run properties the level imposes ──
  bold: boolean;
  italic: boolean;
  underline: boolean;
  allCaps: boolean;
  /** Text case applied at compile time (Word styles can't express title/sentence). */
  textCase?: 'upper' | 'title' | 'sentence';
  /** Hex without '#'. '000000' means "inherit body colour". */
  fontColor: string;

  // ── Font (resolved ONCE here so engine, canvas and preview never disagree) ──
  /** The level's own font if set, else Typography's `bodyFont`. Undefined only
   *  when neither is set — consumers keep their own last-resort default. */
  fontFamily?: string;
  /** Points. The level's own size if set, else Typography's `bodyFontSize`. */
  fontSizePt?: number;
  /** True when the level carries its own `font` (differs from "same as Typography"). */
  fontFamilyOverridden: boolean;
  /** True when the level carries its own `fontSize`. */
  fontSizeOverridden: boolean;

  /** The numbering scheme for this level. */
  numbering: CsiLevelFormat;
}

const DEFAULT_LEVEL: LevelStyle = {
  level: 1,
  bold: false,
  allCaps: false,
  italic: false,
  underline: false,
  fontColor: '000000',
  indent: 0,
  hangingIndent: 0,
  spaceBefore: 0,
  spaceAfter: 0,
  keepWithNext: false,
  keepLinesTogether: false,
  noSpaceSameStyle: false,
};

/**
 * Resolve ONE level's layout from the forma.
 *
 * The indent maths is the engine's law, stated once:
 *   Word's `w:left` is the WRAP edge; the numbered first line starts at
 *   `left - hanging`. So a forma's "indent" (where text starts) plus its
 *   "hangingIndent" (the number column width) is the wrap edge, and the
 *   hanging pulls the first line back to the indent. A NEGATIVE hanging
 *   means the designer dragged the first line past the numbering — Word
 *   expresses that as `w:firstLine`, never a negative `w:hanging`.
 */
export function resolveLevelLayout(
  config: StandardConfig | null | undefined,
  levelIndex: number
): BodyLevelLayout {
  const lvl: LevelStyle = (config?.levels || [])[levelIndex] || {
    ...DEFAULT_LEVEL,
    level: levelIndex + 1,
  };
  const formats = getCsiLevelFormats(config);
  const typo = (config?.docSettings || {}) as Partial<
    Pick<StandardConfig['docSettings'], 'bodyFont' | 'bodyFontSize'>
  >;

  const indentTw = inToTwip(lvl.indent || 0);
  const hangTw = inToTwip(lvl.hangingIndent || 0);
  const leftTwips = Math.max(0, indentTw + hangTw);

  return {
    level: levelIndex + 1,
    leftTwips,
    hangingTwips: hangTw >= 0 ? hangTw : 0,
    firstLineTwips: hangTw < 0 ? -hangTw : 0,
    spaceBeforeTwips: ptToTwip(lvl.spaceBefore || 0),
    spaceAfterTwips: ptToTwip(lvl.spaceAfter || 0),
    contextualSpacing: !!lvl.noSpaceSameStyle,
    keepWithNext: !!lvl.keepWithNext,
    keepLinesTogether: !!lvl.keepLinesTogether,
    justify: lvl.justify || 'left',
    bold: !!lvl.bold,
    italic: !!lvl.italic,
    underline: !!lvl.underline,
    allCaps: !!lvl.allCaps,
    textCase: lvl.textCase,
    fontColor: lvl.fontColor || '000000',
    fontFamily: lvl.font || typo.bodyFont || undefined,
    fontSizePt: lvl.fontSize || typo.bodyFontSize || undefined,
    fontFamilyOverridden: !!lvl.font,
    fontSizeOverridden: !!lvl.fontSize,
    numbering: formats[levelIndex] || CSI_LEVELS_DEFAULT[levelIndex],
  };
}

/** All nine levels resolved. Index 0 = level 1. */
export function resolveBodyLayout(config: StandardConfig | null | undefined): BodyLevelLayout[] {
  return Array.from({ length: 9 }, (_, i) => resolveLevelLayout(config, i));
}

// ─── Page geometry (shared by canvas + export preview) ───

export interface BodyPageGeometry {
  pageWidthIn: number;
  pageHeightIn: number;
  marginTopIn: number;
  marginBottomIn: number;
  marginLeftIn: number;
  marginRightIn: number;
  /** Usable text column width. */
  contentWidthIn: number;
  /** Usable text column height (between the margins). */
  contentHeightIn: number;
  bodyFont: string;
  bodyFontSizePt: number;
  lineSpacing: number;
  widowOrphan: boolean;
  firstPageDifferent: boolean;
  oddEvenDifferent: boolean;
}

export function resolvePageGeometry(config: StandardConfig | null | undefined): BodyPageGeometry {
  const d = (config?.docSettings || {}) as Partial<StandardConfig['docSettings']>;
  const pageWidthIn = d.pageWidth || 8.5;
  const pageHeightIn = d.pageHeight || 11;
  const marginTopIn = d.marginTop ?? 1;
  const marginBottomIn = d.marginBottom ?? 1;
  const marginLeftIn = d.marginLeft ?? 1;
  const marginRightIn = d.marginRight ?? 1;
  return {
    pageWidthIn,
    pageHeightIn,
    marginTopIn,
    marginBottomIn,
    marginLeftIn,
    marginRightIn,
    contentWidthIn: Math.max(0.5, pageWidthIn - marginLeftIn - marginRightIn),
    contentHeightIn: Math.max(0.5, pageHeightIn - marginTopIn - marginBottomIn),
    bodyFont: d.bodyFont || 'Times New Roman',
    bodyFontSizePt: d.bodyFontSize || 12,
    lineSpacing: d.lineSpacing || 1,
    widowOrphan: d.widowOrphan !== false,
    firstPageDifferent: !!d.firstPageDifferent,
    oddEvenDifferent: !!d.oddEvenDifferent,
  };
}

// ─── CSS emission (the Designer canvas + any DOM preview) ───

/** The subset of CSS this module dictates. Callers merge it into their own. */
export interface BodyParagraphCss {
  paddingLeft: string;
  textIndent: string;
  marginTop: string;
  marginBottom: string;
  textAlign: 'left' | 'center' | 'right' | 'justify';
  fontWeight: number;
  fontStyle: 'normal' | 'italic';
  textDecoration: 'none' | 'underline';
  /** 'uppercase' is byte-for-byte what Word's `<w:caps/>` does — a pure,
   *  non-destructive display bit, so it's exact WYSIWYG. 'capitalize' is
   *  Title Case's closest CSS equivalent for LIVE, still-being-typed text
   *  (the .docx export instead rewrites the real text at compile time —
   *  see `applyTextCase` — because Word has no title-case style bit to
   *  mirror here). Sentence case has no safe CSS equivalent at all once a
   *  level's number-column widget sits in the same flow (see
   *  `effectiveTextCase`'s doc comment), so it renders 'none' live; the
   *  compiled .docx is still correct. */
  textTransform: 'none' | 'uppercase' | 'capitalize';
  color?: string;
  /** Present ONLY when the level overrides Typography's font — inheriting
   *  levels keep following the sheet's body font, so they render
   *  pixel-identical to before per-level fonts existed. */
  fontFamily?: string;
  /** CSS px (from points), present ONLY when the level overrides Typography's size. */
  fontSize?: string;
}

/** The case a level's text actually renders in — `textCase` wins over the
 *  legacy `allCaps` boolean (formas saved before `textCase` existed). One
 *  helper so the compile-time rewrite (`applyTextCase`) and the live-canvas
 *  CSS approximation (`bodyParagraphCss`) can't drift apart on what "this
 *  level's case" means. */
export function effectiveTextCase(
  layout: Pick<BodyLevelLayout, 'textCase' | 'allCaps'>
): 'upper' | 'title' | 'sentence' | undefined {
  return layout.textCase ?? (layout.allCaps ? 'upper' : undefined);
}

/**
 * Turn one level's layout decisions into paragraph CSS, in Word's model:
 * `padding-left` is the WRAP edge and `text-indent` is negative by the
 * hanging amount, so the number sits in its own column and wrapped lines
 * align under the text — exactly what the .docx does.
 *
 * `sameStyleAsPrev` implements Word's contextual spacing.
 */
export function bodyParagraphCss(
  layout: BodyLevelLayout,
  opts: { sameStyleAsPrev?: boolean; sameStyleAsNext?: boolean; continuation?: boolean } = {}
): BodyParagraphCss {
  const leftIn = layout.leftTwips / 1440;
  const hangIn = layout.hangingTwips / 1440;
  const firstIn = layout.firstLineTwips / 1440;
  const suppressBefore = layout.contextualSpacing && !!opts.sameStyleAsPrev;
  // Word's contextualSpacing zeroes BOTH sides of a same-style boundary —
  // the preview used to keep the previous paragraph's space-after, showing a
  // gap the .docx doesn't print.
  const suppressAfter = layout.contextualSpacing && !!opts.sameStyleAsNext;
  // A continuation paragraph carries no number, so its first line starts at
  // the wrap edge like every other line.
  const textIndentIn = opts.continuation ? 0 : firstIn - hangIn;
  const caseMode = effectiveTextCase(layout);
  return {
    paddingLeft: `${inToPx(leftIn)}px`,
    textIndent: `${inToPx(textIndentIn)}px`,
    marginTop: `${suppressBefore ? 0 : ptToPx(layout.spaceBeforeTwips / 20)}px`,
    marginBottom: `${suppressAfter ? 0 : ptToPx(layout.spaceAfterTwips / 20)}px`,
    textAlign: layout.justify,
    fontWeight: layout.bold ? 700 : 400,
    fontStyle: layout.italic ? 'italic' : 'normal',
    textDecoration: layout.underline ? 'underline' : 'none',
    textTransform:
      caseMode === 'upper' ? 'uppercase' : caseMode === 'title' ? 'capitalize' : 'none',
    ...(layout.fontColor && layout.fontColor !== '000000' ? { color: `#${layout.fontColor}` } : {}),
    ...(layout.fontFamilyOverridden && layout.fontFamily ? { fontFamily: layout.fontFamily } : {}),
    ...(layout.fontSizeOverridden && layout.fontSizePt
      ? { fontSize: `${ptToPx(layout.fontSizePt)}px` }
      : {}),
  };
}

/**
 * Width reserved for the number column of a level, in CSS px. Word gives the
 * number `hanging` worth of room and tabs to the wrap edge; when a forma
 * leaves no hanging, Word falls to the next default tab stop (0.5").
 */
export function numberColumnPx(layout: BodyLevelLayout): number {
  return inToPx((layout.hangingTwips || inToTwip(0.5)) / 1440);
}

/** Apply a level's `textCase` to a block's text (Word styles can't). */
export function applyTextCase(text: string, layout: BodyLevelLayout): string {
  const mode = effectiveTextCase(layout);
  if (!mode || !text) return text;
  if (mode === 'upper') return text.toUpperCase();
  if (mode === 'sentence') {
    const lower = text.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }
  // title — ONE brain with zone-layout.ts's toTitleCase (Task 6, 2026-08-21):
  // small words lowercase unless first/last, acronyms preserved/dictionary-
  // capped. This body use and the header/footer/zone use must never drift.
  return toTitleCase(text);
}
